import { createTheme } from "@mui/material";

export const FONT_FAMILY =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"';
export const MONO_FONT_FAMILY =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, "Courier New", monospace';

const BRAND_GRADIENT =
  "linear-gradient(135deg, #ff5a3c 0%, #f0468a 48%, #8b3dff 100%)";

// Design tokens shared by MUI (useTheme) and styled-components (props.theme).
// The first block is the current token set, the "legacy" block further down
// keeps the names older pages still read.
interface AppTokens {
  name: string;
  fontFamily: string;
  monoFontFamily: string;
  // Flame gradient of the app icon, for the few brand moments
  brandGradient: string;

  // Surfaces
  bodyBackground: string;
  pageBackground: string;
  navBg: string;
  menuBg: string;
  cardBg: string;
  surfaceRaised: string;
  surfaceHover: string;
  surfaceActive: string;
  inputBg: string;
  overlayBg: string;

  // Lines
  border: string;
  borderStrong: string;

  // Text
  textColor: string;
  secondaryTextColor: string;
  mutedTextColor: string;

  radius: {
    sm: number;
    md: number;
    lg: number;
  };
  shadow: {
    sm: string;
    md: string;
    lg: string;
  };

  colors: {
    primary: string;
    primaryHover: string;
    primarySoft: string;
    secondary: string;
    green: string;
    greenSoft: string;
    purple: string;
    purpleSoft: string;
    gray: string;
    graySoft: string;
    blue: string;
    blueSoft: string;
    error: string;
    red: string;
    redSoft: string;
    orange: string;
    orangeSoft: string;
    background: string;
    gray100: string;
    gray200: string;
    gray600: string;
    gray700: string;
    gray800: string;
    gray900: string;
  };

  // Legacy
  buttonBg: string;
  buttonBgSelected: string;
  buttonFg: string;
  buttonHoverBg: string;
  badgeBg: string;
  badgeFg: string;
  linkHoverBg: string;
  linkColor: string;
  linkHoverColor: string;
  appearChipBg: string;
  appearChipFg: string;
  listItemHoverBg: string;
  listItemOnBodyHoverBg: string;
  typingIndicator: {
    bg: string;
    fg: string;
  };
  divider: {
    rgba: number;
  };
}

declare module "@mui/material/styles" {
  interface Theme extends AppTokens {}
  // allow configuration using `createTheme`
  interface ThemeOptions extends Partial<AppTokens> {}
}

const darkTokens: AppTokens = {
  name: "dark",
  fontFamily: FONT_FAMILY,
  monoFontFamily: MONO_FONT_FAMILY,
  brandGradient: BRAND_GRADIENT,

  bodyBackground: "#0b0d10",
  pageBackground: "#0b0d10",
  navBg: "#0f1115",
  menuBg: "#0f1115",
  cardBg: "#14171c",
  surfaceRaised: "#1c2027",
  surfaceHover: "rgba(255, 255, 255, 0.05)",
  surfaceActive: "rgba(255, 255, 255, 0.09)",
  inputBg: "#181c22",
  overlayBg: "rgba(4, 6, 9, 0.72)",

  border: "rgba(255, 255, 255, 0.08)",
  borderStrong: "rgba(255, 255, 255, 0.16)",

  // Off-white on purpose: pure white on the near-black surface is harsh
  textColor: "#c2c7d0",
  secondaryTextColor: "#9098a5",
  mutedTextColor: "#6b7380",

  radius: { sm: 6, md: 10, lg: 14 },
  shadow: {
    sm: "0 1px 2px rgba(0, 0, 0, 0.4)",
    md: "0 6px 20px rgba(0, 0, 0, 0.45)",
    lg: "0 18px 48px rgba(0, 0, 0, 0.6)",
  },

  colors: {
    primary: "#f0508c",
    primaryHover: "#f36c9e",
    primarySoft: "rgba(240, 80, 140, 0.14)",
    secondary: "#dedede",
    green: "#34c77b",
    greenSoft: "rgba(52, 199, 123, 0.15)",
    purple: "#a78bfa",
    purpleSoft: "rgba(167, 139, 250, 0.16)",
    gray: "#8b93a1",
    graySoft: "rgba(139, 147, 161, 0.16)",
    blue: "#6aa9ff",
    blueSoft: "rgba(106, 169, 255, 0.15)",
    error: "#f0554a",
    red: "#f0554a",
    redSoft: "rgba(240, 85, 74, 0.15)",
    orange: "#f5a524",
    orangeSoft: "rgba(245, 165, 36, 0.15)",
    background: "#14171c",
    gray100: "#1c2027",
    gray200: "rgba(255, 255, 255, 0.12)",
    gray600: "#8b93a1",
    gray700: "#9098a5",
    gray800: "#adb3bd",
    gray900: "#c2c7d0",
  },

  buttonBg: "#1c2027",
  buttonBgSelected: "rgba(106, 169, 255, 0.16)",
  buttonFg: "#c2c7d0",
  buttonHoverBg: "#252a33",
  badgeBg: "#1c2027",
  badgeFg: "#c2c7d0",
  linkHoverBg: "rgba(255, 255, 255, 0.05)",
  linkColor: "#f58ab0",
  linkHoverColor: "#f58ab0",
  appearChipBg: "#6d00ff",
  appearChipFg: "#FFF",
  listItemHoverBg: "#1c2027",
  listItemOnBodyHoverBg: "#181c22",
  typingIndicator: {
    bg: "#0b0d10",
    fg: "#1c2027",
  },
  divider: {
    rgba: 200,
  },
};

const lightTokens: AppTokens = {
  name: "light",
  fontFamily: FONT_FAMILY,
  monoFontFamily: MONO_FONT_FAMILY,
  brandGradient: BRAND_GRADIENT,

  bodyBackground: "#f5f6f8",
  pageBackground: "#f5f6f8",
  navBg: "#ffffff",
  menuBg: "#ffffff",
  cardBg: "#ffffff",
  surfaceRaised: "#ffffff",
  surfaceHover: "rgba(15, 23, 42, 0.045)",
  surfaceActive: "rgba(15, 23, 42, 0.08)",
  inputBg: "#f1f3f6",
  overlayBg: "rgba(15, 23, 42, 0.45)",

  border: "rgba(15, 23, 42, 0.1)",
  borderStrong: "rgba(15, 23, 42, 0.2)",

  textColor: "#11151c",
  secondaryTextColor: "#5a6372",
  mutedTextColor: "#8a92a0",

  radius: { sm: 6, md: 10, lg: 14 },
  shadow: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.08)",
    md: "0 6px 20px rgba(15, 23, 42, 0.1)",
    lg: "0 18px 48px rgba(15, 23, 42, 0.18)",
  },

  colors: {
    primary: "#dc2f7b",
    primaryHover: "#c4246a",
    primarySoft: "rgba(220, 47, 123, 0.1)",
    secondary: "#dedede",
    green: "#1f9d57",
    greenSoft: "rgba(31, 157, 87, 0.12)",
    purple: "#7c4ddb",
    purpleSoft: "rgba(124, 77, 219, 0.12)",
    gray: "#69717e",
    graySoft: "rgba(105, 113, 126, 0.14)",
    blue: "#2f6fd6",
    blueSoft: "rgba(47, 111, 214, 0.12)",
    error: "#d9463b",
    red: "#d9463b",
    redSoft: "rgba(217, 70, 59, 0.12)",
    orange: "#d98a00",
    orangeSoft: "rgba(217, 138, 0, 0.14)",
    background: "#ffffff",
    gray100: "#f1f3f6",
    gray200: "rgba(15, 23, 42, 0.12)",
    gray600: "#69717e",
    gray700: "#5a6372",
    gray800: "#2b323d",
    gray900: "#11151c",
  },

  buttonBg: "#eceef2",
  buttonBgSelected: "#e6f0ff",
  buttonFg: "#11151c",
  buttonHoverBg: "#dfe2e8",
  badgeBg: "#f1f3f6",
  badgeFg: "#5a6372",
  linkHoverBg: "rgba(15, 23, 42, 0.045)",
  linkColor: "#c4246a",
  linkHoverColor: "#c4246a",
  appearChipBg: "#6d00ff",
  appearChipFg: "#FFF",
  listItemHoverBg: "#f1f3f6",
  listItemOnBodyHoverBg: "#e8ebef",
  typingIndicator: {
    bg: "#b4b5b9",
    fg: "#e6e7ed",
  },
  divider: {
    rgba: 0,
  },
};

const buildTheme = (tokens: AppTokens) => {
  const isDark = tokens.name === "dark";
  const flatPaper = {
    boxShadow: "none",
    border: `1px solid ${tokens.border}`,
  };
  const floatingPaper = {
    boxShadow: tokens.shadow.lg,
    border: `1px solid ${tokens.border}`,
    backgroundColor: tokens.surfaceRaised,
  };

  return createTheme({
    ...tokens,
    palette: {
      mode: isDark ? "dark" : "light",
      primary: {
        main: tokens.colors.primary,
        contrastText: "#fff",
      },
      secondary: {
        main: isDark ? "#c995d6" : "#e0357d",
        contrastText: "#fff",
      },
      error: { main: tokens.colors.red, contrastText: "#fff" },
      warning: { main: tokens.colors.orange, contrastText: "#fff" },
      success: {
        main: isDark ? "#27b36b" : tokens.colors.green,
        contrastText: "#fff",
      },
      info: { main: tokens.colors.blue, contrastText: "#fff" },
      background: {
        default: tokens.bodyBackground,
        paper: tokens.cardBg,
      },
      text: {
        primary: tokens.textColor,
        secondary: tokens.secondaryTextColor,
        disabled: tokens.mutedTextColor,
      },
      divider: tokens.border,
      action: {
        hover: tokens.surfaceHover,
        selected: tokens.surfaceActive,
      },
    },
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily: tokens.fontFamily,
      h1: { fontSize: "2rem", fontWeight: 650, letterSpacing: "-0.02em" },
      h2: { fontSize: "1.75rem", fontWeight: 650, letterSpacing: "-0.02em" },
      h3: { fontSize: "1.5rem", fontWeight: 650, letterSpacing: "-0.015em" },
      h4: { fontSize: "1.375rem", fontWeight: 650, letterSpacing: "-0.015em" },
      h5: { fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em" },
      h6: { fontSize: "1rem", fontWeight: 600, letterSpacing: "-0.005em" },
      subtitle1: { fontSize: "0.9375rem", fontWeight: 500 },
      subtitle2: { fontSize: "0.8125rem", fontWeight: 600 },
      body1: { fontSize: "0.9375rem" },
      body2: { fontSize: "0.8125rem" },
      caption: { fontSize: "0.75rem" },
      button: { textTransform: "none", fontWeight: 600 },
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 8,
            lineHeight: 1.4,
            transition:
              "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
          },
          sizeSmall: { padding: "4px 10px", fontSize: "0.8125rem" },
          sizeMedium: { padding: "7px 14px", fontSize: "0.875rem" },
          outlined: {
            borderColor: tokens.borderStrong,
          },
          outlinedInherit: {
            borderColor: tokens.borderStrong,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            transition: "background-color 0.15s ease, color 0.15s ease",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: "none" },
          elevation1: flatPaper,
          elevation2: flatPaper,
          elevation3: flatPaper,
          elevation4: flatPaper,
          elevation8: floatingPaper,
          elevation24: floatingPaper,
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 12 },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 20,
            "&:last-child": { paddingBottom: 20 },
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.inputBg,
            border: `1px solid ${tokens.border}`,
            borderRadius: 10,
            padding: 3,
            gap: 2,
            flexWrap: "wrap",
          },
          grouped: {
            border: "0 !important",
            borderRadius: "7px !important",
            margin: "0 !important",
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            color: tokens.secondaryTextColor,
            fontWeight: 500,
            fontSize: "0.8125rem",
            lineHeight: 1.4,
            padding: "5px 12px",
            border: 0,
            transition: "background-color 0.15s ease, color 0.15s ease",
            "&:hover": {
              backgroundColor: tokens.surfaceHover,
              color: tokens.textColor,
            },
            "&.Mui-selected, &.Mui-selected:hover": {
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.11)" : "#ffffff",
              color: tokens.textColor,
              boxShadow: tokens.shadow.sm,
            },
          },
          sizeSmall: {
            padding: "5px 12px",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: tokens.inputBg,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.border,
              transition: "border-color 0.15s ease",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.borderStrong,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: tokens.colors.primary,
              borderWidth: 1,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: tokens.secondaryTextColor,
          },
        },
      },
      MuiTooltip: {
        defaultProps: {
          enterDelay: 250,
          disableInteractive: true,
        },
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? "#2a2f38" : "#11151c",
            color: "#fff",
            fontSize: "0.75rem",
            fontWeight: 500,
            borderRadius: 6,
            padding: "5px 8px",
            boxShadow: tokens.shadow.md,
          },
          arrow: {
            color: isDark ? "#2a2f38" : "#11151c",
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            "&:not(.MuiBackdrop-invisible)": {
              backgroundColor: tokens.overlayBg,
              backdropFilter: "blur(3px)",
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: "1rem",
            fontWeight: 600,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "12px 20px 16px",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: "0.875rem",
            borderRadius: 6,
            margin: "2px 6px",
            padding: "6px 10px",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${tokens.border}`,
            fontSize: "0.8438rem",
          },
          head: {
            color: tokens.secondaryTextColor,
            fontSize: "0.75rem",
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            border: `1px solid ${tokens.border}`,
            alignItems: "center",
          },
          standardError: {
            backgroundColor: tokens.colors.redSoft,
            borderColor: tokens.colors.redSoft,
          },
          standardWarning: {
            backgroundColor: tokens.colors.orangeSoft,
            borderColor: tokens.colors.orangeSoft,
          },
          standardSuccess: {
            backgroundColor: tokens.colors.greenSoft,
            borderColor: tokens.colors.greenSoft,
          },
          standardInfo: {
            backgroundColor: tokens.colors.blueSoft,
            borderColor: tokens.colors.blueSoft,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40 },
          indicator: { height: 2, borderRadius: 2 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            minHeight: 40,
            fontSize: "0.875rem",
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: tokens.border },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.surfaceRaised,
            color: tokens.secondaryTextColor,
            fontSize: "0.875rem",
            fontWeight: 600,
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.surfaceActive,
          },
        },
      },
    },
  });
};

export const lightTheme = buildTheme(lightTokens);
export const darkTheme = buildTheme(darkTokens);
